import uniqueId from 'lodash/uniqueId';
import Data from '../data';
import Menu from '../menu';
import Utils from '../utils';
import SettingsIdentifiers from './SettingsIdentifiers.enum';

const { Capability } = Data;
const { ItemType } = Menu;
const { Session } = Utils;

export const SettingsMenuBuilder = activeTab => {
	const { maxImageCount = 0 } = Session.getAccountInfo()?.customerDetails;

	// check for activeTab in its menu group to determine its active and collapsed state
	const isActive = {
		common: Object.values(SettingsIdentifiers.Common).includes(activeTab),
		mail: Object.values(SettingsIdentifiers.Mail).includes(activeTab),
		orders: Object.values(SettingsIdentifiers.Orders).includes(activeTab),
		brandedFeatures: Object.values(
			SettingsIdentifiers.BrandedFeatures
		).includes(activeTab),
	};

	const commonSettingsMenu = {
		id: parseInt(uniqueId(), 10),
		type: ItemType.Group,
		title: 'Common',
		active: isActive.common,
		collapsed: !isActive.common,
		visible: true,
		items: [
			{
				id: parseInt(uniqueId(), 10),
				type: ItemType.Badge,
				title: 'General',
				visible: true,
				active: activeTab === SettingsIdentifiers.Common.General,
				mobileTitle: 'General Common Settings',
				data: { tabId: SettingsIdentifiers.Common.General },
				dataqa: 'settings-common-general',
			},
			{
				id: parseInt(uniqueId(), 10),
				type: ItemType.Badge,
				title: 'Ship From Addresses',
				visible: true,
				active: activeTab === SettingsIdentifiers.Common.ShipFrom,
				mobileTitle: 'Shipping Addresses',
				data: { tabId: SettingsIdentifiers.Common.ShipFrom },
				dataqa: 'settings-common-ship-from-addresses',
			},
			{
				id: parseInt(uniqueId(), 10),
				type: ItemType.Badge,
				title: 'Carriers',
				visible:
					Session.hasCapability(Capability.Ups) && !Session.isFreemiumUser(),
				active: activeTab === SettingsIdentifiers.Common.Carriers,
				mobileTitle: 'Carriers',
				data: { tabId: SettingsIdentifiers.Common.Carriers },
				dataqa: 'settings-common-carriers',
			},
			{
				id: parseInt(uniqueId(), 10),
				type: ItemType.Badge,
				title: 'Service Options',
				visible: true,
				active: activeTab === SettingsIdentifiers.Common.ServiceOptions,
				mobileTitle: 'Service Options',
				data: { tabId: SettingsIdentifiers.Common.ServiceOptions },
				dataqa: 'settings-common-service-options',
			},
			{
				id: parseInt(uniqueId(), 10),
				type: ItemType.Badge,
				title: 'Label Customization',
				visible:
					maxImageCount > 1 && Session.hasCapability(Capability.LabelLogo),
				active: activeTab === SettingsIdentifiers.Common.LabelCustomization,
				mobileTitle: 'Label Customization',
				data: { tabId: SettingsIdentifiers.Common.LabelCustomization },
				dataqa: 'settings-common-label-customization',
			},
		],
	};

	const mailSettingsMenu = {
		id: parseInt(uniqueId(), 10),
		type: ItemType.Group,
		title: 'Mail',
		active: isActive.mail,
		collapsed: !isActive.mail,
		visible: true,
		items: [
			{
				id: parseInt(uniqueId(), 10),
				type: ItemType.Badge,
				title: 'General',
				visible: true,
				active: activeTab === SettingsIdentifiers.Mail.General,
				mobileTitle: 'General Mail Settings',
				data: { tabId: SettingsIdentifiers.Mail.General },
				dataqa: 'settings-mail-general',
			},
			{
				id: parseInt(uniqueId(), 10),
				type: ItemType.Badge,
				title: 'Print On Options',
				visible: true,
				active: activeTab === SettingsIdentifiers.Mail.PrintOnOptions,
				mobileTitle: 'Print On Options',
				data: { tabId: SettingsIdentifiers.Mail.PrintOnOptions },
				dataqa: 'settings-mail-print-on-options',
			},
			{
				id: parseInt(uniqueId(), 10),
				type: ItemType.Badge,
				title: 'Refund Assist',
				visible: Session.hasCapability(Capability.RefundAssist),
				active: activeTab === SettingsIdentifiers.Mail.RefundAssist,
				mobileTitle: 'Refund Assist',
				data: { tabId: SettingsIdentifiers.Mail.RefundAssist },
				dataqa: 'settings-mail-refund-assist',
			},
		],
	};

	const ordersSettingsMenu = {
		id: parseInt(uniqueId(), 10),
		type: ItemType.Group,
		title: 'Orders',
		active: isActive.orders,
		collapsed: !isActive.orders,
		visible: true,
		items: [
			{
				id: parseInt(uniqueId(), 10),
				type: ItemType.Badge,
				title: 'Stores',
				visible: true,
				active: activeTab === SettingsIdentifiers.Orders.Stores,
				mobileTitle: 'Stores Settings',
				data: { tabId: SettingsIdentifiers.Orders.Stores },
				dataqa: 'settings-orders-stores',
			},
			{
				id: parseInt(uniqueId(), 10),
				type: ItemType.Badge,
				title: 'International',
				visible: true,
				active: activeTab === SettingsIdentifiers.Orders.International,
				mobileTitle: 'International Settings',
				data: { tabId: SettingsIdentifiers.Orders.International },
				dataqa: 'settings-orders-international',
			},
			{
				id: parseInt(uniqueId(), 10),
				title: 'Automation Rules',
				visible: true,
				active: activeTab === SettingsIdentifiers.Orders.AutomationRules,
				mobileTitle: 'Automation Rules',
				data: { tabId: SettingsIdentifiers.Orders.AutomationRules },
				dataqa: 'settings-orders-automation-rules',
			},
			{
				id: parseInt(uniqueId(), 10),
				type: ItemType.Badge,
				title: 'Label Messages',
				visible: true,
				active: activeTab === SettingsIdentifiers.Orders.LabelMessages,
				mobileTitle: 'Label Message Settings',
				data: { tabId: SettingsIdentifiers.Orders.LabelMessages },
				dataqa: 'settings-orders-label-messages',
			},
			{
				id: parseInt(uniqueId(), 10),
				type: ItemType.Badge,
				title: 'Packing Slips',
				visible: true,
				active: activeTab === SettingsIdentifiers.Orders.PackingSlips,
				mobileTitle: 'Packing Slips Settings',
				data: { tabId: SettingsIdentifiers.Orders.PackingSlips },
				dataqa: 'settings-orders-packing-slips',
			},
		],
	};

	const brandedFeaturesSettingsMenu = {
		id: parseInt(uniqueId(), 10),
		type: ItemType.Group,
		title: 'Branded Features',
		active: isActive.brandedFeatures,
		collapsed: !isActive.brandedFeatures,
		visible:
			Session.hasCapability(Capability.BrandedTracking) ||
			Session.hasCapability(Capability.BrandedEmails),
		items: [
			{
				id: parseInt(uniqueId(), 10),
				type: ItemType.Badge,
				title: 'Brand Settings',
				visible: Session.hasCapability(Capability.BrandedTracking),
				active: activeTab === SettingsIdentifiers.BrandedFeatures.BrandSettings,
				mobileTitle: 'Settings',
				data: { tabId: SettingsIdentifiers.BrandedFeatures.BrandSettings },
				dataqa: 'settings-branded-features-brand-settings',
			},
		],
	};

	const items = [
		commonSettingsMenu,
		mailSettingsMenu,
		ordersSettingsMenu,
		brandedFeaturesSettingsMenu,
	];

	return items.filter(groupItem => {
		// return the 1st-level group item that should be visible
		if (groupItem.visible === true) {
			// return the 2nd-level child items that should be visible
			groupItem.items = groupItem.items.filter(
				badgeItem => badgeItem.visible === true
			);

			return groupItem;
		}

		return null;
	});
};
